import React, { useState, useEffect } from "react";
import "../assets/scss/components/main-nav.scss";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGlobalContext } from "../contexts/GlobalContext";
import { useUserContext } from "../contexts/UserContext";
import { appModules } from "../routes/appModulesConfig";
import { MOD_USERS, RBAC_RESOURCE_USER_ROLES, USER_ROLES_PATH } from "../contstants";

const MainNav = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [currentPath, setCurrentPath] = useState(location.pathname);
  const currentUser = useUserContext();
  const { allowedModules, setshowSideMenu } = useGlobalContext();

  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location]);

  const isActiveLink = (path) => currentPath === path;
  const hasActiveLink = (path) => currentPath.startsWith(path);
  const handleMenuClick = () => setshowSideMenu(false);

  const hasPermission = (resource, action) => currentUser.can(resource, action);
  const hasDynamicPermission = (resource, action) =>
    currentUser.hasPermission?.(resource, action);
  // Defaults to visible for non-migrated users and unmapped modules.
  const canAccessModule = (module) =>
    currentUser.canAccessModule ? currentUser.canAccessModule(module) : true;

  // Hold a skeleton until permissions load, else the full menu paints then
  // collapses once gating kicks in (a "blink" on refresh). permissionsLoaded
  // resolves even for non-migrated users, so this never sticks.
  if (currentUser.userData?.id && !currentUser.permissionsLoaded) {
    return (
      <nav className="main-nav app-nav" id="main-menu">
        <ul className="nav-skeleton" aria-hidden="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <li key={i} className="nav-skeleton-item" />
          ))}
        </ul>
      </nav>
    );
  }

  return (
    <nav className="main-nav app-nav" id="main-menu">
      <ul>
        {appModules.map((item) => {
          const Icon = item.icon;
          const isOpen = hasActiveLink(item.path);
          const validChildren = (item.children || []).filter((child) => {
            return (
              allowedModules.includes(child.module) &&
              hasPermission(child.module, "read") &&
              canAccessModule(child.module)
            );
          });

          // User Roles: dynamic users gated on the permission; legacy
          // owner/admin by role while the IIP surface is up. Insert after Users.
          const canManageUserRoles =
            hasDynamicPermission(RBAC_RESOURCE_USER_ROLES, "read") ||
            (currentUser.iipAvailable &&
              !currentUser.hasDynamicPermissions?.() &&
              ["owner", "admin"].includes(currentUser.userData?.role));
          const childrenToRender = [...validChildren];
          if (item.label === "Settings" && canManageUserRoles) {
            const userRolesChild = {
              label: "User Roles",
              path: USER_ROLES_PATH,
              module: "user_roles",
            };
            const usersIndex = childrenToRender.findIndex(
              (child) => child.module === MOD_USERS
            );
            if (usersIndex >= 0) {
              childrenToRender.splice(usersIndex + 1, 0, userRolesChild);
            } else {
              childrenToRender.push(userRolesChild);
            }
          }

          // Hide the parent when it has no visible children and no read access.
          if (
            childrenToRender.length === 0 &&
            (!allowedModules.includes(item.module) ||
              !hasPermission(item.module, "read") ||
              !canAccessModule(item.module))
          ) {
            return null;
          }

          const isSettingMenu = item.label === "Settings";
          // Point the parent at its own page only if reachable; else the first
          // visible child, so a scoped role doesn't land on Access Denied.
          const parentAccessible =
            allowedModules.includes(item.module) &&
            hasPermission(item.module, "read") &&
            canAccessModule(item.module);
          const parentLink = parentAccessible
            ? item.path
            : childrenToRender[0]?.path || item.path;
          return (
            <React.Fragment key={item.module || item.path || item.label}>
              {isSettingMenu && (
                <li
                  className="sidebar-separator"
                  id="settings-separator"
                  role="separator"
                  aria-hidden="true"
                />
              )}

              <li
                id={`menu-${item.module || item.path || item.label}`}
                className={isOpen ? "open" : ""}
              >
                <Link to={parentLink}>
                  {Icon && <Icon />}
                  {t(item.label)}
                </Link>
                {childrenToRender.length > 0 && (
                  <ul className="sub-menu">
                    {childrenToRender.map((child) => (
                      <li
                        key={child.module || child.path || child.label}
                        id={`submenu-${
                          child.module || child.path || child.label
                        }`}
                      >
                        <Link
                          className={isActiveLink(child.path) ? "active" : ""}
                          to={child.path}
                          onClick={handleMenuClick}
                        >
                          {t(child.label)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ul>
    </nav>
  );
};

export default MainNav;
